<template>
    <div>
        <div class="mr-5 mb-3 flex-1">
            <base-input
                v-model="employeeTabelCodeModel"
                max-length="20"
                class="mb-1"
                :disabled="employeeTabelCodeReadonly"
            >Табельный код</base-input>
        </div>
        <div class="mr-5 mb-3 flex-1">
            <div class="text-xs text-gray pb-1">Подразделение</div>
            <dropdown v-model="departmentModel" :options="departments" class="text-xs">
                {{ cutText(departmentLabel, labelLength) }}
            </dropdown>
        </div>
        <div class="mr-5 mb-3 flex-1">
            <div class="text-xs text-gray pb-1">Должность</div>
            <dropdown v-model="positionModel" :options="positions" class="text-xs">
                {{ cutText(positionLabel, labelLength) }}
            </dropdown>
        </div>
        <div class="flex">
            <div class="mr-5 mb-2 flex-1">
                <div class="text-xs text-gray pb-1">Дата начала</div>
                <AppDateInput v-model="hiredDateModel" />
            </div>
            <div class="mr-5 mb-2 flex-1">
                <div class="text-xs text-gray pb-1">Дата окончания</div>
                <AppDateInput v-model="firedDateModel" />
            </div>
        </div>
        <div class="flex">
            <div class="mr-5 mb-2 flex-1">
                <base-input v-model="percentageModel" max-length="3" class="mb-1" @keydown="onPercentageKeydown">Доля рабочих часов</base-input>
            </div>
            <div class="mr-5 mb-2 flex-1">
                <div class="text-xs text-gray pb-1">Доступность</div>
                <dropdown v-model="shiftScheduleModel" :options="shiftSchedules" class="text-xs">
                    {{ cutText(shiftScheduleLabel, labelLength) }}
                </dropdown>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppDateInput from '@/ui/AppDateInput.vue'
import type { Option } from '@/types'
import type { Dayjs } from 'dayjs'

type DateInputValue = Dayjs | [Dayjs, Dayjs] | Dayjs[] | null | undefined

interface Props {
    employeeTabelCode: string
    department: Option | null
    departments: Option[]
    position: Option | null
    positions: Option[]
    hiredDate: DateInputValue
    firedDate: DateInputValue
    percentage: string
    shiftSchedule: Option | null
    shiftSchedules: Option[]
    labelLength?: number
    employeeTabelCodeReadonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    labelLength: 24,
    employeeTabelCodeReadonly: false,
})

const emit = defineEmits<{
    (e: 'update:employeeTabelCode', value: string): void
    (e: 'update:department', value: Option | null): void
    (e: 'update:position', value: Option | null): void
    (e: 'update:hiredDate', value: DateInputValue): void
    (e: 'update:firedDate', value: DateInputValue): void
    (e: 'update:percentage', value: string): void
    (e: 'update:shiftSchedule', value: Option | null): void
    (e: 'change'): void
}>()

const employeeTabelCodeModel = computed({
    get: () => props.employeeTabelCode,
    set: (value: string) => {
        emit('update:employeeTabelCode', value)
        emit('change')
    },
})

const departmentLabel = computed(() => props.department?.name ?? 'Выберите подразделение')
const departmentModel = computed({
    get: () => props.department,
    set: (value: Option | null) => {
        emit('update:department', value)
        emit('change')
    },
})

const positionLabel = computed(() => props.position?.name ?? 'Выберите должность')
const positionModel = computed({
    get: () => props.position,
    set: (value: Option | null) => {
        emit('update:position', value)
        emit('change')
    },
})

const hiredDateModel = computed<DateInputValue>({
    get: () => props.hiredDate,
    set: (value: DateInputValue) => {
        emit('update:hiredDate', value)
        emit('change')
    },
})

const firedDateModel = computed<DateInputValue>({
    get: () => props.firedDate,
    set: (value: DateInputValue) => {
        emit('update:firedDate', value)
        emit('change')
    },
})

const percentageModel = computed({
    get: () => props.percentage,
    set: (value: string) => {
        emit('update:percentage', value.replace(/\D/g, ''))
        emit('change')
    },
})

const onPercentageKeydown = (event: KeyboardEvent) => {
    if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
    ) {
        return
    }

    if (!/^\d$/.test(event.key)) {
        event.preventDefault()
    }
}

const shiftScheduleLabel = computed(() => props.shiftSchedule?.name ?? 'Выберите расписание')
const shiftScheduleModel = computed({
    get: () => props.shiftSchedule,
    set: (value: Option | null) => {
        emit('update:shiftSchedule', value)
        emit('change')
    },
})

const cutText = (value: string, length: number) => {
    if (value.length <= length) return value
    return `${value.slice(0, length)}...`
}
</script>
